import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { objectToSearchString } from 'serialize-query-params'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'

import { Alert, PasswordMaskField } from 'components/common'
import { SocialLoginButtons } from 'containers/common'
import { useAuth } from 'hooks/useAuth'
import { AuthResponse } from 'models/response'
import { isPhoneNumber } from 'utils/common'
import { trueErrorMessage } from 'utils/error'
import { isPhoneNoAccountError } from 'utils/invite-accept'
import { NO_PHONE_ACCOUNT_MESSAGE, emailSignInHint, isAccountRefusal } from 'utils/phone-accounts'
import type { PhoneAccount } from 'services/auth'
import { usePhoneAccountLookup } from 'hooks/usePhoneAccountLookup'
import {
	PhoneAccountCard,
	PhoneAccountChooser,
	PhoneLoginMethod,
	PhoneLoginMethodPicker,
} from 'components/molecules/phone-account-confirm'
import PasskeyAuth from 'containers/passkey-auth'
import { Button } from 'components/atoms/button'
import { CodeInput } from 'components/atoms/code-input'
import { Turnstile } from 'components/atoms/turnstile'
import { useTurnstile } from 'hooks/useTurnstile'
import { useCountdown } from 'hooks/use-countdown'
import { useEmailCodeSignIn } from 'hooks/use-email-code-sign-in'
import { EmailCodeForm } from 'components/organisms/forms/email-code-form'
import { EmailCodeNoAccount } from 'components/organisms/forms/email-code-form/outcomes'
import { trackEvent, AUTH_EVENTS } from 'services/mixpanel'
import {
	FORGOT_PASSWORD_MESSAGE,
	INVALID_EMAIL_MESSAGE,
	NO_PASSWORD_MESSAGE,
	isNoPasswordSignInError,
	isValidEmail,
	isVerifyOtpResponse,
	looksLikeEmail,
} from 'utils/email-code-sign-in'

/**
 * Where the form is. The phone steps carry the account they act on, so "a phone code screen for an account that
 * was never chosen" is not a state this component can be in. `emailCode` is the code emailed by email/start
 * (sign-in and forgot password; sign-up stays the password form).
 */
type LoginStep =
	| { kind: 'username' }
	| { kind: 'chooseAccount' }
	| { kind: 'confirmPhone'; account: PhoneAccount }
	| { kind: 'phoneCode'; account: PhoneAccount }
	// `account` is set when the password belongs to an account picked from a phone number's list: its handle names it
	| { kind: 'password'; account?: PhoneAccount }
	| { kind: 'emailCode' }

/** Steps after the phone lookup, before any code is sent */
const isPhoneAccountStep = (step: LoginStep) => step.kind === 'chooseAccount' || step.kind === 'confirmPhone'

const stepTitle = (step: LoginStep, forgotPassword = false): string => {
	if (step.kind === 'username') return forgotPassword ? 'Forgot your password?' : 'Login'
	if (step.kind === 'emailCode') return 'Check your email'
	if (step.kind === 'chooseAccount') return 'Choose your account'
	if (step.kind === 'confirmPhone') return 'Choose a way to log in'
	return step.kind === 'phoneCode' ? 'Enter Verification Code' : 'Enter Password'
}

const submitLabel = (step: LoginStep, retryIn?: string): string => {
	if (step.kind === 'username') return retryIn ? `Try again in ${retryIn}` : 'Continue'
	if (isPhoneAccountStep(step)) return 'Continue'
	return step.kind === 'phoneCode' ? 'Verify Code' : 'Sign In'
}

const usernameStepSchema = Yup.object().shape({
	userName: Yup.string().required('Please enter your email, username, or phone number'),
})

const passwordStepSchema = Yup.object().shape({
	userName: Yup.string().required('Please enter your email, username, or phone number'),
	password: Yup.string().required('Please enter your password'),
})

const otpStepSchema = Yup.object().shape({
	userName: Yup.string().required('Please enter your phone number'),
	otpCode: Yup.string()
		.required('Please enter the verification code')
		.length(6, 'Verification code must be 6 digits'),
})

interface LoginFormProps {
	initialEmail?: string
	/** Pre-filled from route state (never the URL); starts phone sign-in right away. */
	initialPhoneNumber?: string
	onSuccess?: (rs: AuthResponse) => void
	isModal?: boolean
	/** Receives the email to prefill when it is "Create an account" that was clicked */
	onSwitchToSignUp?: (email?: string) => void
	inviteId?: string
	fromForgotPassword?: boolean
	redirectTo?: string | null
}

interface LoginFormData {
	userName: string
	password?: string
	otpCode?: string
}

export const LoginForm = ({
	initialEmail,
	initialPhoneNumber,
	onSuccess,
	isModal = false,
	onSwitchToSignUp,
	inviteId,
	fromForgotPassword = false,
	redirectTo = null,
}: LoginFormProps) => {
	const [message, setMessage] = useState<any>(null)
	const [submitting, setSubmitting] = useState(false)
	// The chosen account rides on the step, so the phone code can never be shown without one
	const [step, setStep] = useState<LoginStep>({ kind: 'username' })
	// Accounts from phone/accounts, confirmed before phone/start texts a code
	const [phoneAccountsFound, setPhoneAccountsFound] = useState<PhoneAccount[]>([])
	const [phoneLoginMethod, setPhoneLoginMethod] = useState<PhoneLoginMethod>('sms')
	const [otpSent, setOtpSent] = useState(false)
	const [nextResendTime, setNextResendTime] = useState<Date | null>(null)
	const [phoneCodeExpiredDate, setPhoneCodeExpiredDate] = useState<Date | null>(() => {
		try {
			const stored = localStorage.getItem('phoneCodeExpiredDate')
			if (stored) {
				const parsed = JSON.parse(stored)
				return parsed ? new Date(parsed) : null
			}
		} catch (error) {
			console.warn('Failed to parse phoneCodeExpiredDate from localStorage:', error)
		}
		return null
	})
	const [resendCountdown, setResendCountdown] = useState<number>(0)
	const [failedAttempts, setFailedAttempts] = useState<number>(0)
	const [blockedUntil, setBlockedUntil] = useState<Date | null>(null)
	const [blockCountdown, setBlockCountdown] = useState<number>(0)
	const { login, loginToPhoneAccount, phoneLogin, phoneVerifyOtp, isPasskeySupported } = useAuth()
	const turnstile = useTurnstile()
	const phoneAccountLookup = usePhoneAccountLookup()
	// "Forgot password?": the same email → code flow, with its own copy (and analytics)
	const [forgotPassword, setForgotPassword] = useState(false)
	const emailCode = useEmailCodeSignIn({
		isModal,
		isForgotPassword: forgotPassword,
		setMessage,
		onSignedIn: rs => onSuccess?.(rs),
	})
	const emailStartRetry = useCountdown(emailCode.retryAt)
	// Email whose code could not be sent (throttled, locked): offer its password from the first step
	const [passwordOfferEmail, setPasswordOfferEmail] = useState<string | null>(null)

	const safeSetPhoneCodeExpiredDate = (date: Date | null) => {
		setPhoneCodeExpiredDate(date)
		try {
			if (date) {
				localStorage.setItem('phoneCodeExpiredDate', JSON.stringify(date.toISOString()))
			} else {
				localStorage.removeItem('phoneCodeExpiredDate')
			}
		} catch (error) {
			console.warn('Failed to save phoneCodeExpiredDate to localStorage:', error)
		}
	}

	const getCurrentValidationSchema = () => {
		if (step.kind === 'phoneCode') return otpStepSchema
		if (step.kind === 'password') return passwordStepSchema
		return usernameStepSchema
	}

	const {
		control,
		register,
		handleSubmit,
		watch,
		reset,
		setValue,
		formState: { errors, isValid, isDirty },
	} = useForm<LoginFormData>({
		resolver: yupResolver(getCurrentValidationSchema()),
		mode: 'onChange',
		defaultValues: {
			userName: initialPhoneNumber || initialEmail || '',
			password: '',
			otpCode: '',
		},
	})

	const watchedUserName = watch('userName')

	// Countdown text for the 429 Retry-After on the phone lookup or the email code, if one is running
	const lookupRetry = isPhoneNumber(watchedUserName)
		? { secondsLeft: phoneAccountLookup.retrySecondsLeft, formatted: phoneAccountLookup.retryFormatted }
		: emailStartRetry
	const lookupRetryIn = lookupRetry.secondsLeft > 0 ? lookupRetry.formatted : undefined

	// Three wrong texted codes lock the texted-code step only: it must not block signing in another way
	const phoneCodeLocked = !!(blockedUntil && blockCountdown > 0) && step.kind === 'phoneCode'

	const isContinueButtonEnabled = () => {
		if (submitting) return false

		if (phoneCodeLocked) return false

		// Nothing to fill in: the user only confirms the account (the phone may be prefilled, so not dirty)
		if (step.kind === 'confirmPhone') return true

		if (step.kind === 'username') {
			// phone/accounts needs a Turnstile token and honours the 429 Retry-After cooldown
			if (isPhoneNumber(watchedUserName) && (!turnstile.token || phoneAccountLookup.retrySecondsLeft > 0)) {
				return false
			}
			// email/start needs a Turnstile token too, and honours its own 429 Retry-After
			if (looksLikeEmail(watchedUserName) && (!turnstile.token || emailStartRetry.secondsLeft > 0)) {
				return false
			}
			return isValid && watchedUserName && watchedUserName.trim().length > 0
		} else {
			return isValid && isDirty
		}
	}

	useEffect(() => {
		if (fromForgotPassword) {
			setMessage({
				description:
					'If an account with the email you entered exists, you will receive instructions to reset your password shortly.',
				type: 'success',
			})
		}
	}, [fromForgotPassword])

	// Forget the stored code expiry once the code runs out (no expiry clock is shown)
	useEffect(() => {
		const expiredDate = phoneCodeExpiredDate
		if (!expiredDate) return

		const timeout = setTimeout(
			() => safeSetPhoneCodeExpiredDate(null),
			Math.max(0, expiredDate.getTime() - Date.now())
		)
		return () => clearTimeout(timeout)
	}, [phoneCodeExpiredDate])

	// Next resend countdown timer effect
	useEffect(() => {
		if (!nextResendTime) return

		const updateNextResendCountdown = () => {
			const now = new Date()
			const timeLeft = Math.max(0, Math.floor((nextResendTime.getTime() - now.getTime()) / 1000))
			setResendCountdown(timeLeft)

			if (timeLeft <= 0) {
				setNextResendTime(null)
			}
		}

		updateNextResendCountdown()
		const interval = setInterval(updateNextResendCountdown, 1000)

		return () => clearInterval(interval)
	}, [nextResendTime])

	// Block countdown timer effect
	useEffect(() => {
		if (!blockedUntil) return

		const updateBlockCountdown = () => {
			const now = new Date()
			const timeLeft = Math.max(0, Math.floor((blockedUntil.getTime() - now.getTime()) / 1000))
			setBlockCountdown(timeLeft)

			if (timeLeft <= 0) {
				setBlockedUntil(null)
				setFailedAttempts(0)
				setMessage({
					description: 'You can now try again.',
					type: 'info',
				})
			}
		}

		updateBlockCountdown()
		const interval = setInterval(updateBlockCountdown, 1000)

		return () => clearInterval(interval)
	}, [blockedUntil])

	const formatCountdown = (seconds: number): string => {
		const minutes = Math.floor(seconds / 60)
		const remainingSeconds = seconds % 60
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
	}

	const formatBlockCountdown = (seconds: number): string => {
		const hours = Math.floor(seconds / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
		const remainingSeconds = seconds % 60

		if (hours > 0) {
			return `${hours}h ${minutes}m ${remainingSeconds}s`
		}
		return `${minutes}m ${remainingSeconds}s`
	}

	// Show which account the phone signs in to before texting a code (phone/accounts, then phone/start)
	const lookupPhoneAccount = async (userName: string) => {
		setSubmitting(true)
		const result = await phoneAccountLookup.lookup(userName, turnstile.token ?? undefined)
		// Turnstile tokens are single-use
		turnstile.reset()
		setSubmitting(false)
		switch (result.kind) {
			case 'found':
				setPhoneAccountsFound(result.accounts)
				// One account: skip the list and go straight to the ways to log in
				if (result.accounts.length === 1) {
					handleSelectPhoneAccount(result.accounts[0])
				} else {
					setStep({ kind: 'chooseAccount' })
				}
				return
			case 'none':
				setMessage({ description: NO_PHONE_ACCOUNT_MESSAGE, type: 'danger' })
				return
			case 'rateLimited':
				setMessage({ description: 'Too many attempts. Please wait before trying again.', type: 'danger' })
				return
			case 'error':
				setMessage({ description: result.message, type: 'danger' })
		}
	}

	// Same screen for sign-in and sign-up: email/start answers the same whether or not the address has an account
	const startEmailCode = async (emailAddress: string) => {
		setSubmitting(true)
		setPasswordOfferEmail(null)
		const sent = await emailCode.start(emailAddress, turnstile.token ?? undefined)
		// Turnstile tokens are single-use
		turnstile.reset()
		setSubmitting(false)
		if (sent) {
			setStep({ kind: 'emailCode' })
		} else {
			setPasswordOfferEmail(emailAddress.trim())
		}
	}

	const handleUsernameStep = (values: LoginFormData) => {
		setMessage(null)
		const { userName } = values

		if (forgotPassword && !isValidEmail(userName)) {
			// Only an email can be sent a code
			setMessage({ description: INVALID_EMAIL_MESSAGE, type: 'danger' })
		} else if (isPhoneNumber(userName) && !forgotPassword) {
			void lookupPhoneAccount(userName)
		} else if (looksLikeEmail(userName) || forgotPassword) {
			if (!isValidEmail(userName)) {
				setMessage({ description: INVALID_EMAIL_MESSAGE, type: 'danger' })
				return
			}
			void startEmailCode(userName)
		} else {
			// A username: only a password can sign it in
			setStep({ kind: 'password' })
			reset({ userName, password: '' })
		}
	}

	const goToPasswordStep = (emailAddress: string) => {
		setMessage(null)
		setForgotPassword(false)
		setStep({ kind: 'password' })
		reset({ userName: emailAddress, password: '' })
	}

	// Shown to everyone: the API deliberately doesn't say whether an email has a password
	const handleContinueWithPassword = (emailAddress: string) => {
		trackEvent(AUTH_EVENTS.EMAIL_CODE_PASSWORD_FALLBACK_CLICKED, {
			from_step: step.kind,
			code_locked: !!emailCode.blockedUntil,
		})
		goToPasswordStep(emailAddress)
	}

	const handleChangeEmail = () => {
		const previousEmail = emailCode.email ?? watchedUserName
		emailCode.reset()
		setStep({ kind: 'username' })
		reset({ userName: previousEmail, password: '', otpCode: '' })
	}

	// "Forgot password?": back to the first step as the email-only code flow. `hint` says which email, when it is only known masked.
	const handleForgotPassword = (hint?: string) => {
		// A phone/start or lookup still in flight would land on this screen afterwards and put the phone step back
		if (submitting) return
		const typed = watchedUserName?.trim() ?? ''
		// The texted-code state belongs to the phone flow: its lockout must not hold up the email code
		setOtpSent(false)
		setNextResendTime(null)
		setResendCountdown(0)
		setFailedAttempts(0)
		setBlockedUntil(null)
		setBlockCountdown(0)
		safeSetPhoneCodeExpiredDate(null)
		emailCode.reset()
		setPasswordOfferEmail(null)
		setPhoneAccountsFound([])
		setForgotPassword(true)
		setStep({ kind: 'username' })
		reset({ userName: looksLikeEmail(typed) ? typed : '', password: '', otpCode: '' })
		setMessage(hint ? { description: hint, type: 'info' } : null)
	}

	const handleBackToLogin = () => {
		emailCode.reset()
		setForgotPassword(false)
		setMessage(null)
		reset({ userName: watchedUserName, password: '', otpCode: '' })
	}

	const getSignUpUrl = (emailAddress?: string) => {
		const search = objectToSearchString({ to: redirectTo, email: emailAddress })
		const query = search ? `?${search}` : ''
		return `/auth/sign-up${query}`
	}

	const handleResendEmailCode = async () => {
		if (!emailCode.email) return
		await emailCode.start(emailCode.email, turnstile.token ?? undefined)
		turnstile.reset()
	}

	// The chosen account is what start/verify target, so a text code works whether one account or several
	const handleSelectPhoneAccount = (account: PhoneAccount) => {
		setPhoneLoginMethod('sms')
		setStep({ kind: 'confirmPhone', account })
	}

	const leavePhoneAccountSteps = () => {
		setPhoneAccountsFound([])
		setStep({ kind: 'username' })
		reset({ userName: '', password: '', otpCode: '' })
	}

	// Same account, password route: straight to the password. The account's handle names it, so no email or username is asked.
	const handleUsePassword = (userName: string, account: PhoneAccount) => {
		setMessage(null)
		setStep({ kind: 'password', account })
		reset({ userName, password: '', otpCode: '' })
	}

	// "Not you?": back to the account list when there is one, otherwise to the first step
	const handleNotYou = () => {
		setMessage(null)
		if (phoneAccountsFound.length > 1) {
			setStep({ kind: 'chooseAccount' })
		} else {
			leavePhoneAccountSteps()
		}
	}

	const startPhoneSignIn = (userName: string, account: PhoneAccount) => {
		setMessage(null)
		setSubmitting(true)

		setFailedAttempts(0)
		setBlockedUntil(null)
		setBlockCountdown(0)

		phoneLogin(
			{ phoneNumber: userName, accountHandle: account.handle }, // Phone login doesn't need password
			(rs: AuthResponse) => {
				setSubmitting(false)
				setStep({ kind: 'phoneCode', account })
				setOtpSent(true)
				reset({ userName, otpCode: '' })

				if (rs.nextResend) {
					const nextResendAt = new Date(rs.nextResend)
					setNextResendTime(nextResendAt)
				}

				if (rs.expiredDate) {
					const expiredDateAt = new Date(rs.expiredDate)
					safeSetPhoneCodeExpiredDate(expiredDateAt)
				}

				if (rs.blockedUntil) {
					const blockedUntilDate = new Date(rs.blockedUntil)
					setBlockedUntil(blockedUntilDate)
				}
			},
			(error: unknown) => {
				setSubmitting(false)
				const message = trueErrorMessage((error as any)?.data)
				if (message.toLowerCase().includes('otp') || message.toLowerCase().includes('verification')) {
					setStep({ kind: 'phoneCode', account })
					setOtpSent(true)
					reset({ userName, otpCode: '' })
					setMessage({
						description: 'Verification code sent to your phone',
						type: 'success',
					})
				} else if (isAccountRefusal(error)) {
					// The account this handle names is gone or no longer holds the number, so retrying this screen
					// cannot work: start over with the number, as a refused handle on verify does.
					leavePhoneAccountSteps()
					setMessage({
						description: 'This sign-in attempt has expired. Enter your phone number again.',
						type: 'danger',
					})
				} else if (isPhoneNoAccountError(message)) {
					// The phone may be on an account but never verified: phone sign-in can't find it
					setMessage({ description: NO_PHONE_ACCOUNT_MESSAGE, type: 'danger' })
				} else {
					setMessage({
						description: message,
						type: 'danger',
					})
				}
			},
			isModal
		)
	}

	// Arrived from "Sign in with a code to this phone": look the account up as soon as Turnstile has a token
	const autoStartedPhoneRef = useRef(false)
	useEffect(() => {
		if (!initialPhoneNumber || autoStartedPhoneRef.current) return
		// The user moved on (edited the number, typed an email, or left the first step): never auto-start later,
		// or a token from the password step would look up the old phone
		if (step.kind !== 'username' || watchedUserName !== initialPhoneNumber) {
			autoStartedPhoneRef.current = true
			return
		}
		if (!turnstile.token) return
		autoStartedPhoneRef.current = true
		handleUsernameStep({ userName: initialPhoneNumber })
	}, [initialPhoneNumber, turnstile.token, step.kind, watchedUserName])

	const phoneVerifyInFlightRef = useRef(false)

	const handlePhoneCodeStep = (values: LoginFormData, account: PhoneAccount) => {
		// The code auto-submits on its 6th digit, so Enter or a click can land in the same tick
		if (phoneVerifyInFlightRef.current) return
		setSubmitting(true)
		setMessage(null)

		if (!values.otpCode) {
			setSubmitting(false)
			setMessage({
				description: 'Please enter the verification code',
				type: 'danger',
			})
			return
		}

		phoneVerifyInFlightRef.current = true
		// The handle chosen on the confirm step is what pins the code to this account
		phoneVerifyOtp(
			{ phoneNumber: values.userName, code: values.otpCode, accountHandle: account.handle },
			(rs: AuthResponse) => {
				phoneVerifyInFlightRef.current = false
				setSubmitting(false)
				setFailedAttempts(0)
				setBlockedUntil(null)
				setBlockCountdown(0)
				if (onSuccess) {
					onSuccess(rs)
				}
			},
			(error: unknown) => {
				phoneVerifyInFlightRef.current = false
				setSubmitting(false)
				// Cleared so the next code can be typed (and auto-submitted) from the start
				setValue('otpCode', '', { shouldValidate: true })

				// Not a refused code: the account is the problem (the handle expired, or the account was removed or
				// changed number), or the request failed for a reason worth retrying. Neither is a wrong code, and
				// counting it would blame the member for attempts they did not make.
				if (!isVerifyOtpResponse((error as any)?.data)) {
					if (isAccountRefusal(error)) {
						// Retrying this screen cannot work, so start over with the number.
						leavePhoneAccountSteps()
						setMessage({
							description: 'This sign-in attempt has expired. Enter your phone number again.',
							type: 'danger',
						})
					} else {
						setMessage({ description: trueErrorMessage((error as any)?.data), type: 'danger' })
					}
					return
				}

				const newFailedAttempts = failedAttempts + 1
				setFailedAttempts(newFailedAttempts)

				if (newFailedAttempts >= 3) {
					const blockUntil = new Date(Date.now() + 30 * 60 * 1000)
					setBlockedUntil(blockUntil)
					setMessage({
						description: `Too many failed attempts. You are blocked for 30 minutes.`,
						type: 'danger',
					})
				} else {
					const remainingAttempts = 3 - newFailedAttempts
					setMessage({
						description: `Invalid verification code. ${remainingAttempts} attempt${
							remainingAttempts !== 1 ? 's' : ''
						} remaining.`,
						type: 'danger',
					})
				}
			}
		)
	}

	const handlePasswordStep = (values: LoginFormData) => {
		setSubmitting(true)
		setMessage(null)

		if (!values.password) {
			setSubmitting(false)
			setMessage({
				description: 'Please enter your password',
				type: 'danger',
			})
			return
		}

		const onSignedIn = (rs: AuthResponse) => {
			setSubmitting(false)
			turnstile.reset()
			if (onSuccess) {
				onSuccess(rs)
			}
		}

		if (step.kind === 'password' && step.account) {
			loginToPhoneAccount(
				{ phoneNumber: values.userName, accountHandle: step.account.handle, password: values.password },
				turnstile.token,
				onSignedIn,
				(error: string) => {
					setSubmitting(false)
					turnstile.reset()
					setMessage({ description: error, type: 'danger' })
				},
				isModal
			)
			return
		}

		login(
			{ userName: values.userName, password: values.password },
			turnstile.token,
			onSignedIn,
			(error: string) => {
				setSubmitting(false)
				turnstile.reset()
				if (isNoPasswordSignInError(error) && emailCode.email === values.userName.trim()) {
					// Back to the code that was already emailed; its resend countdown is kept
					setStep({ kind: 'emailCode' })
					setMessage({ description: NO_PASSWORD_MESSAGE, type: 'info' })
					return
				}
				setMessage({
					description: error,
					type: 'danger',
				})
			},
			isModal
		)
	}

	const onSubmit = (values: LoginFormData) => {
		switch (step.kind) {
			case 'username':
				handleUsernameStep(values)
				return
			case 'confirmPhone':
				if (phoneLoginMethod === 'sms') {
					startPhoneSignIn(values.userName, step.account)
				} else {
					handleUsePassword(values.userName, step.account)
				}
				return
			case 'phoneCode':
				handlePhoneCodeStep(values, step.account)
				return
			case 'password':
				handlePasswordStep(values)
		}
	}

	const handleResendOtp = (account: PhoneAccount) => {
		setSubmitting(true)
		phoneLogin(
			// The handle chosen on the confirm step texts the account the code was sent for
			{ phoneNumber: watchedUserName, accountHandle: account.handle },
			(rs: AuthResponse) => {
				setSubmitting(false)
				setMessage({
					description: 'Verification code has been resent',
					type: 'success',
				})

				if (rs.nextResend) {
					const nextResendAt = new Date(rs.nextResend)
					setNextResendTime(nextResendAt)
				}

				if (rs.expiredDate) {
					const expiredDateAt = new Date(rs.expiredDate)
					safeSetPhoneCodeExpiredDate(expiredDateAt)
				}

				if (rs.blockedUntil) {
					const blockedUntilDate = new Date(rs.blockedUntil)
					setBlockedUntil(blockedUntilDate)
				}
			},
			(error: unknown) => {
				setSubmitting(false)
				if (isAccountRefusal(error)) {
					// The handle names an account that is gone or no longer holds the number: a resend would fail the
					// same way, so start over with the number.
					leavePhoneAccountSteps()
					setMessage({
						description: 'This sign-in attempt has expired. Enter your phone number again.',
						type: 'danger',
					})
					return
				}
				setMessage({
					description: trueErrorMessage((error as any)?.data),
					type: 'danger',
				})
			}
		)
	}

	return (
		<div className={clsx(isModal ? '' : 'w-full')}>
			<div className={clsx(isModal ? '' : 'w-full')}>
				{!isModal && (
					<h1 className="text-3xl font-bold text-center text-white mb-4">
						{emailCode.outcome ? stepTitle({ kind: 'username' }) : stepTitle(step, forgotPassword)}
					</h1>
				)}
				{/* Alert has no live-region semantics of its own. This region is always mounted, so a message that arrives
				    later is announced; errors interrupt, everything else waits its turn. */}
				<div aria-live={message?.type === 'danger' ? 'assertive' : 'polite'} aria-atomic="true">
					<Alert message={message} />
				</div>
				{step.kind === 'username' && forgotPassword && (
					<p className="mb-4 text-center text-secondary-text">{FORGOT_PASSWORD_MESSAGE}</p>
				)}
				{/* Not shown in forgot-password or once a code is sent; hidden on the confirm step, which tells the user they already have an account */}
				{!forgotPassword && step.kind !== 'emailCode' && (
					<div className={clsx('mb-4', { hidden: isPhoneAccountStep(step) })}>
						<div className="text-center text-white">
							Don't have an account?{' '}
							{isModal && onSwitchToSignUp ? (
								<Button
									variant="link"
									className="inline-flex -my-2.5 py-2.5"
									onClick={() => onSwitchToSignUp()}
								>
									Sign up
								</Button>
							) : (
								<Link className="inline-flex" to={getSignUpUrl()} state={{ inviteId }}>
									<Button variant="link" className="-my-2.5 py-2.5">
										Sign up
									</Button>
								</Link>
							)}
						</div>
					</div>
				)}
				{step.kind === 'emailCode' && emailCode.email && emailCode.outcome?.kind === 'noAccount' && (
					<EmailCodeNoAccount
						email={emailCode.email}
						onTryAnotherEmail={handleChangeEmail}
						createAccount={
							isModal && onSwitchToSignUp ? (
								<Button
									variant="primary"
									fullWidth
									size="lg"
									onClick={() => onSwitchToSignUp(emailCode.email as string)}
								>
									Create an account
								</Button>
							) : (
								<Link className="block w-full" to={getSignUpUrl(emailCode.email)} state={{ inviteId }}>
									<Button variant="primary" fullWidth size="lg">
										Create an account
									</Button>
								</Link>
							)
						}
					/>
				)}
				{step.kind === 'emailCode' && emailCode.email && !emailCode.outcome && (
					<EmailCodeForm
						email={emailCode.email}
						nextResend={emailCode.resendAt}
						blockedUntil={emailCode.blockedUntil}
						codeExpired={emailCode.codeExpired}
						sending={emailCode.sending}
						verifying={emailCode.verifying}
						resetKey={emailCode.resetKey}
						resendReady={!!turnstile.token}
						onVerify={emailCode.verify}
						onResend={() => void handleResendEmailCode()}
						onChangeEmail={handleChangeEmail}
						onContinueWithPassword={() => handleContinueWithPassword(emailCode.email as string)}
					>
						<div className="flex justify-center">
							<Turnstile
								ref={turnstile.turnstileRef}
								onSuccess={turnstile.onSuccess}
								onError={turnstile.onError}
								onExpire={turnstile.onExpire}
								theme="dark"
							/>
						</div>
					</EmailCodeForm>
				)}
				<form
					onSubmit={handleSubmit(onSubmit)}
					// The code step renders its own form above; this one keeps its field state for "Change email"
					className={clsx({ hidden: step.kind === 'emailCode' })}
				>
					<div className="space-y-4 text-left">
						{/* Username field */}
						<div>
							{step.kind === 'username' ? (
								<div>
									<input
										id="username"
										type="text"
										className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
										placeholder={forgotPassword ? 'Email' : 'Email, Username, or Phone Number'}
										aria-label={
											forgotPassword ? 'Email address' : 'Email, username, or phone number'
										}
										autoComplete={forgotPassword ? 'email' : 'username'}
										// Locked while the phone lookup runs, so the result always matches the number shown
										readOnly={submitting}
										{...register('userName')}
									/>
									{errors.userName && (
										<div className="text-secondary-red mt-2 text-sm">
											{errors.userName.message as string}
										</div>
									)}
								</div>
							) : (
								<div className="mb-2">
									<div className="flex items-center justify-between pb-3 px-4 border-b border-white-25">
										<div className="text-white text-lg font-medium">{watchedUserName}</div>
										<button
											type="button"
											onClick={() => {
												emailCode.reset()
												setStep({ kind: 'username' })
												setOtpSent(false)
												setNextResendTime(null)
												setResendCountdown(0)
												setFailedAttempts(0)
												setBlockedUntil(null)
												setBlockCountdown(0)
												setMessage(null)
												safeSetPhoneCodeExpiredDate(null)
												reset({ userName: watchedUserName, password: '', otpCode: '' })
											}}
											className="text-primary hover:text-primary-hover transition-colors disabled:opacity-50"
											// A late phone/start reply would otherwise reopen the code step for the old number
											disabled={submitting}
											title="Change username"
										>
											<FontAwesomeIcon icon={['fas', 'edit']} size="lg" />
										</button>
									</div>
								</div>
							)}
						</div>

						{step.kind === 'password' && step.account && <PhoneAccountCard account={step.account} />}
						{step.kind === 'chooseAccount' && (
							<PhoneAccountChooser accounts={phoneAccountsFound} onSelect={handleSelectPhoneAccount} />
						)}
						{step.kind === 'confirmPhone' && (
							<PhoneLoginMethodPicker
								account={step.account}
								phoneNumber={watchedUserName}
								method={phoneLoginMethod}
								forgotDisabled={submitting}
								onForgotPassword={() => handleForgotPassword(emailSignInHint([step.account]))}
								onMethodChange={setPhoneLoginMethod}
							/>
						)}

						{/* Credentials field - the texted code or the password, never both */}
						{(step.kind === 'phoneCode' || step.kind === 'password') && (
							<div>
								{step.kind === 'phoneCode' ? (
									<div>
										<label
											htmlFor="phone-sign-in-code"
											className="mb-2 block text-sm font-medium text-primary-text"
										>
											Enter the 6-digit code we texted you
										</label>
										<Controller
											control={control}
											name="otpCode"
											render={({ field }) => (
												<CodeInput
													ref={field.ref}
													id="phone-sign-in-code"
													// Mounts with the code step, so focus lands on it on arrival
													autoFocus
													value={field.value || ''}
													onChange={value => {
														field.onChange(value)
														// Same as the email code: the 6th digit submits
														if (
															value.length === 6 &&
															!submitting &&
															!(blockedUntil && blockCountdown > 0)
														) {
															void handleSubmit(onSubmit)()
														}
													}}
													onBlur={field.onBlur}
													// Read-only, not disabled, while verifying: keeps focus and the keyboard, and a
													// code typed meanwhile can't be wiped by that request's failure
													readOnly={submitting}
													disabled={!!(blockedUntil && blockCountdown > 0)}
												/>
											)}
										/>
										{otpSent && (
											<div className="mt-4 flex flex-col items-center justify-between gap-2">
												{blockedUntil && blockCountdown > 0 ? (
													<div className="text-secondary-red text-sm">
														Account blocked. Try again in{' '}
														{formatBlockCountdown(blockCountdown)}
													</div>
												) : (
													<Button
														variant="link"
														// Plain text link: no pill from the disabled blur and rounding while the cooldown runs
														className="!p-0 !rounded-none disabled:!backdrop-blur-none"
															onClick={() => handleResendOtp(step.account)}
														disabled={submitting || resendCountdown > 0}
													>
														{submitting
															? 'Resending...'
															: resendCountdown > 0
																? `Resend in ${formatCountdown(resendCountdown)}`
																: 'Resend code'}
													</Button>
												)}
											</div>
										)}
									</div>
								) : (
									<div>
										<Controller
											control={control}
											name="password"
											render={({ field }) => (
												<PasswordMaskField
													id="password"
													className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
													placeholder="Password"
													autoComplete="current-password"
													value={field.value ?? ''}
													onChange={field.onChange}
													onBlur={field.onBlur}
												/>
											)}
										/>
										{errors.password && (
											<div className="text-secondary-red mt-2 text-sm">
												{errors.password.message as string}
											</div>
										)}
									</div>
								)}
							</div>
						)}
					</div>
					{/* phone/accounts, email/start and password/sign-in all need a token */}
					{(step.kind === 'password' || step.kind === 'username') && (
						<div className="mt-4 flex justify-center">
							<Turnstile
								ref={turnstile.turnstileRef}
								onSuccess={turnstile.onSuccess}
								onError={turnstile.onError}
								onExpire={turnstile.onExpire}
								theme="dark"
							/>
						</div>
					)}
					<div className="mt-8">
						<Button
							variant="primary"
							fullWidth
							size="lg"
							type="submit"
							// Picking a row in the account list moves on by itself
							className={clsx({ hidden: step.kind === 'chooseAccount' })}
							disabled={!isContinueButtonEnabled()}
						>
							{submitting && (
								<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="mr-1" />
							)}
							{phoneCodeLocked
								? `Blocked (${formatBlockCountdown(blockCountdown)})`
								: submitLabel(step, lookupRetryIn)}
						</Button>

						{step.kind === 'username' &&
							passwordOfferEmail &&
							passwordOfferEmail === watchedUserName?.trim() && (
								<Button
									variant="outline"
									fullWidth
									size="lg"
									type="button"
									className="mt-3"
									disabled={submitting}
									onClick={() => handleContinueWithPassword(passwordOfferEmail)}
								>
									Continue with password
								</Button>
							)}

						{step.kind === 'confirmPhone' && (
							<Button
								variant="outline"
								fullWidth
								size="lg"
								type="button"
								className="mt-3"
								disabled={submitting}
								onClick={handleNotYou}
							>
								Not you?
							</Button>
						)}

						{/* Passkey Login Section - show in username step */}
						{step.kind === 'username' && !forgotPassword && isPasskeySupported() && (
							<>
								<div className="mt-6 flex items-center">
									<div className="flex-1 border-t border-white-25"></div>
									<span className="px-4 text-sm text-white/50">or</span>
									<div className="flex-1 border-t border-white-25"></div>
								</div>
								<div className="mt-6">
									<PasskeyAuth
										variant="login"
										onSuccess={(response: AuthResponse) => {
											if (onSuccess) {
												onSuccess(response)
											}
										}}
										onError={(error: string) => {
											setMessage({
												description: error,
												type: 'danger',
											})
										}}
										disabled={submitting}
									/>
								</div>
							</>
						)}
					</div>
				</form>

				{/* Forgot password is the emailed-code flow. The confirm-account step has its own link; the code step needs none. */}
				{!isPhoneAccountStep(step) && step.kind !== 'emailCode' && (
					<div className="mt-5 flex items-center justify-center">
						<Button
							variant="link"
							className="-my-2.5 py-2.5"
							disabled={submitting}
							onClick={forgotPassword ? handleBackToLogin : () => handleForgotPassword()}
						>
							{forgotPassword ? 'Back to login' : 'Forgot your password?'}
						</Button>
					</div>
				)}

				{/* Social login - only show in username step */}
				{step.kind === 'username' && !forgotPassword && (
					<div className="mt-4">
						<h3 className="text-center text-white mb-6">Or Log in via</h3>
						<SocialLoginButtons isModal={isModal} />
					</div>
				)}
			</div>
		</div>
	)
}
